import { memo } from "react";
import { LinearProgress, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { faCalendarXmark, faWarehouse } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { formatNumber } from "../../../utilities/format";
import i18n from "../../../i18n";
import { UNIT_LABELS } from "../../../DTOs/DrillBlast/ExplosivesDTO";
import { EXPLOSIVES_STORE_TARGET } from "../../../utilities/helpers/dashboardLinks";
import { AccessLink } from "../../AccessLink/AccessLink";
import { Pill } from "../../DrillBlast/Indicators";
import { neqUsageColour } from "../../DrillBlast/usageColour";
import { expiresSoon, explosivesStockQuery } from "../../Hooks/useExplosivesLookups";
import { useDashboardCard } from "../../Contexts/DashboardCardContext";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";

const kg = (value: number) => formatNumber(value, { maximumFractionDigits: 0 });

export const ExplosivesStockCard: React.FC = memo(() => {
  const { isFullScreen } = useDashboardCard();
  const { data: stock = [], isLoading, isError } = useQuery({ ...explosivesStockQuery, retry: false });

  if (isLoading) return <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col" />;

  if (isError || stock.length === 0) {
    return (
      <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col">
        <DashboardCardEmptyState isFullScreen={isFullScreen} icon={faWarehouse} title={i18n.t("dashboard:explosivesStock.emptyTitle")}
          subtitle={i18n.t("dashboard:explosivesStock.empty")} />
      </Paper>
    );
  }

  return (
    <Paper className="!shadow-none !border-none !w-full h-full relative !rounded-none !bg-theme-surface-2 flex flex-col">
      <div className="animate-fade-in flex flex-col gap-2 px-3 py-3 overflow-y-auto">
        {stock.map(s => {
          const licensed = s.magazine.licensedNeqKg;
          const used = licensed ? Math.min(100, s.neqKg / licensed * 100) : null;
          const ageing = s.balances.filter(b => b.quantity > 0 && expiresSoon(b.expiresOn));
          return (
            <AccessLink key={s.magazine.id} target={EXPLOSIVES_STORE_TARGET}
              className="rounded-xl border border-theme-border bg-theme-surface px-3 py-2 flex flex-col gap-1 transition"
              linkedClassName="cursor-pointer hover:border-theme-table-heading-contrast hover:bg-theme-surface-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Typography variant="body2" className="!font-bold !text-theme-text">{s.magazine.name}</Typography>
                <div className="flex flex-wrap gap-1">
                  {s.parkedMovements > 0 && <Pill tone="danger">{i18n.t("dashboard:explosivesStock.waiting", { count: s.parkedMovements })}</Pill>}
                  {s.hasPendingStocktake && <Pill tone="warning">{i18n.t("dashboard:explosivesStock.stocktakeToApprove")}</Pill>}
                </div>
              </div>
              <Typography variant="caption" className="!text-theme-text-muted">
                {licensed
                  ? i18n.t("dashboard:explosivesStock.neqOfLicensed", { neq: kg(s.neqKg), licensed: kg(licensed) })
                  : i18n.t("dashboard:explosivesStock.neq", { neq: kg(s.neqKg) })}
              </Typography>
              {used != null && <LinearProgress variant="determinate" value={used} color={neqUsageColour(used)} />}
              {ageing.map(b => (
                <Typography key={`${b.blastMaterialId}-${b.lotId ?? 0}`} variant="caption" className="!text-theme-warning flex items-center gap-1">
                  <FontAwesomeIcon icon={faCalendarXmark} />
                  {i18n.t(b.expiresOn!.diffNow("days").days < 0 ? "dashboard:explosivesStock.expired" : "dashboard:explosivesStock.useBy", {
                    batch: b.lotCode ? i18n.t("dashboard:explosivesStock.batchWithLot", { material: b.material, lot: b.lotCode }) : b.material,
                    quantity: formatNumber(b.quantity),
                    unit: UNIT_LABELS[b.unit],
                    date: b.expiresOn!.toFormat("d MMM"),
                  })}
                </Typography>
              ))}
            </AccessLink>
          );
        })}
      </div>
    </Paper>
  );
});
